import { Head, usePage } from "@inertiajs/react"
import { Download } from "lucide-react"
import { AdminShell } from "@/components/AdminShell"
import { AdminPageHeader, EmptyState } from "@/components/admin/AdminPageHeader"
import { FilterBar } from "@/components/admin/FilterBar"
import { plural } from "@/components/admin/format"
import { CapNote, ResponsiveList } from "@/components/admin/ResponsiveList"
import { Badge } from "@/components/ui/badge"
import { formatDay } from "@/lib/dates"
import type { AdminLead, AdminLeadsIndexProps, PageProps } from "@/types/inertia"

const TITLE = "Leads"
const DESCRIPTION =
  "Everyone who downloaded a label mock-up or the brochure through the email gate, newest first, with their marketing consent."

export default function AdminLeadsIndex() {
  const { props } = usePage<PageProps<AdminLeadsIndexProps>>()
  const { leads, total, limit, filters, sources } = props

  return (
    <>
      <Head title={TITLE}>
        <meta name="description" content={DESCRIPTION} />
        <meta property="og:title" content={TITLE} />
        <meta property="og:description" content={DESCRIPTION} />
      </Head>
      <AdminShell>
        <AdminPageHeader
          title="Leads"
          description="Everyone who downloaded a mock-up or the brochure through the email gate, newest first."
        />

        <FilterBar
          path="/admin/leads"
          idPrefix="leads-filter"
          values={filters}
          fields={[{ kind: "select", name: "source", label: "Downloaded", options: sources, allLabel: "Mock-ups and brochure" }]}
        />

        <p className="mt-6" aria-live="polite">
          {plural(total, "lead")}
          {filters.source ? (total === 1 ? " matches." : " match.") : "."}
        </p>
        <CapNote limit={limit} total={total} noun="leads" />

        <div className="mt-3">
          {leads.length === 0 ? (
            <EmptyState>
              {filters.source ? "No leads from this download yet." : "No leads yet. Everyone who downloads through the email gate is listed here."}
            </EmptyState>
          ) : (
            <ResponsiveList
              label="Leads"
              items={leads}
              getKey={(lead) => lead.id}
              card={(lead) => <LeadCard lead={lead} />}
              columns={[
                {
                  key: "email",
                  header: "Email",
                  cell: (lead) => (
                    <div className="min-w-0 break-all">
                      <a href={`mailto:${lead.email}`}>{lead.email}</a>
                    </div>
                  ),
                },
                {
                  key: "first_name",
                  header: "First name",
                  cell: (lead) => lead.first_name ?? "—",
                },
                {
                  key: "source",
                  header: "Downloaded",
                  cell: (lead) => <SourceBadge lead={lead} />,
                },
                {
                  key: "consent",
                  header: "Consent",
                  cell: (lead) => <ConsentBadge lead={lead} />,
                },
                {
                  key: "mockup",
                  header: "Mock-up",
                  cell: (lead) => <MockupLink lead={lead} />,
                },
                {
                  key: "date",
                  header: "Date",
                  className: "whitespace-nowrap",
                  cell: (lead) => formatDay(lead.created_at),
                },
              ]}
            />
          )}
        </div>
      </AdminShell>
    </>
  )
}

function SourceBadge({ lead }: { lead: AdminLead }) {
  return <Badge tone={lead.source === "brochure_download" ? "neutral" : "accent"}>{lead.source_label}</Badge>
}

/** Marketing consent as words, never colour alone. */
function ConsentBadge({ lead }: { lead: AdminLead }) {
  return lead.marketing_consent ? <Badge tone="accent">Consented</Badge> : <Badge tone="muted">No consent</Badge>
}

function MockupLink({ lead }: { lead: AdminLead }) {
  const mockup = lead.mockup
  if (!mockup) return <span className="text-ink-muted">—</span>
  return (
    <div className="flex items-center gap-2">
      {mockup.preview_url && (
        <a
          href={mockup.preview_url}
          target="_blank"
          rel="noopener noreferrer"
          className="flex h-11 w-9 shrink-0 items-center justify-center overflow-hidden rounded-sm bg-surface"
        >
          <img
            src={mockup.preview_url}
            alt={`Open ${mockup.title}`}
            loading="lazy"
            decoding="async"
            className="h-full w-full object-contain"
          />
        </a>
      )}
      {mockup.download_url ? (
        <a href={mockup.download_url} download className="inline-flex min-h-11 items-center gap-1 text-sm">
          <Download className="h-4 w-4" aria-hidden="true" />
          Download<span className="sr-only"> {mockup.title}</span>
        </a>
      ) : (
        <span className="text-xs text-ink-muted">{mockup.title}</span>
      )}
    </div>
  )
}

function LeadCard({ lead }: { lead: AdminLead }) {
  return (
    <div className="px-4 py-3">
      <div className="flex flex-wrap items-baseline justify-between gap-x-3">
        <span className="min-w-0 break-all text-sm font-medium text-ink-display">
          {lead.first_name ?? "No name given"}
        </span>
        <span className="text-xs text-ink-muted">{formatDay(lead.created_at)}</span>
      </div>
      <div className="break-all text-sm">
        <a href={`mailto:${lead.email}`} className="inline-flex min-h-11 items-center">
          {lead.email}
        </a>
      </div>
      <div className="flex flex-wrap items-center gap-1.5">
        <SourceBadge lead={lead} />
        <ConsentBadge lead={lead} />
      </div>
      {lead.mockup && (
        <div className="mt-2">
          <MockupLink lead={lead} />
        </div>
      )}
    </div>
  )
}
